import { Injectable, OnDestroy } from '@angular/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { ITableData } from '../../../models/reports/ITableData';
import { IDataItem } from '../../../models/reports/IDataItem';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { ReportTextAlignment } from '../../../models/reports/ReportTextAlignment';
import { TranslateService } from '@ngx-translate/core';
import { CommonFunctionsService } from '../../common-functions.service';
import { IndexDBService } from '../../common/indexDB.service';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { Material, MaterialData, SQWebArea, SQWebSystem, SubSystem } from '../../../models/dtos';
import { SQWebAreaChildrenBase } from '../../../models/SQWebAreaChildrenBase';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { ReportCheckboxes } from '../../../models/reports/ReportCheckboxes';
import { MaterialCostData } from '../../../models/reports/MaterialCostData';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { CommonDataService } from '../../common-data.service';
import { MaterialIndicators } from '../../../models/MaterialIndicators';

@Injectable()
export class SolMaterialByCountryService implements OnDestroy {
  private materialByCountryInfoExpandedList: ITableData[] = [];
  private materialByCountryInfoList: ITableData[] = [];
  private countryMaterialMaps: Map<string, Map<string, MaterialCostData>> = new Map();
  private countryTotals: Map<string, number> = new Map();
  selectedReportCheckboxes: ReportCheckboxes;

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly translate: TranslateService,
    private readonly indexDBService: IndexDBService,
    private readonly multilingualCurrencyPipe: MultilingualCurrencyPipe
  ) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true): Promise<void> {
    this.selectedReportCheckboxes = this.reportsCommonService.getReportsCheckboxes();
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const reportData: IReportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: await this.generateTableData(reportId),
      pageOrientation: pageOrientation,
      internalReport: internalReport
    };
    this.reportsCommonService.setSelectedReport(reportData);
  }
  
  private async generateTableData(reportId: ReportList): Promise<ITableData[]> {
    
    await this.generateMaterialByCountryReport();

    let selectedReportData: ITableData[] = [];
    switch (reportId) {
      case ReportList.PartsByCountryViewModel:
        selectedReportData = this.selectedReportCheckboxes.expanded.checked ? this.materialByCountryInfoExpandedList : this.materialByCountryInfoList;
        break;
      default:
        break;
    }
    
    this.clearMemory();
    return selectedReportData;
  }

  private async generateMaterialByCountryReport(): Promise<void> {

    this.materialByCountryInfoExpandedList = [];
    this.materialByCountryInfoList = [];    

    // Process areas and systems like the working material service
    const areas = this.commonDataService.getSQWebProject().areas;

    for (const area of areas) {
      if (area.isItemVisible) {
        await this.processAreaForMaterialByCountry(area);
      }
    }

    // Create tables for each country
    this.createCountryTables();
    
  }

  private async processAreaForMaterialByCountry(area: SQWebArea): Promise<void> {
    
    for (const child of area.children) {
      const childItem = child as SQWebAreaChildren;
      if (childItem.nodeType !== CONSTANTS.NODETYPE_CONTROLLER && !childItem.isItemVisible) {
        continue;
      }
      
      if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
        // Recursively process sub-areas
        const subArea = childItem as SQWebArea;
        if (subArea.isItemVisible) {
          await this.processAreaForMaterialByCountry(subArea);
        }
      } else {
        // Process system-level items (cast back to system types since we know it's not an Area)
        const systemItem = childItem as SQWebAreaChildrenBase;
        
        await this.processSystemForMaterialByCountry(systemItem);
      }
    }
  }

  private async processSystemForMaterialByCountry(systemItem: SQWebAreaChildrenBase): Promise<void> {
    let materialDataGuid = systemItem.itemUniqueId;

    if (systemItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
      const system = systemItem as SQWebSystem;
      if (system.systemTypicalOfHelperUniqueId) {
        materialDataGuid = this.commonFunctionsService.getCollectionHolderId(system);
      }
    }

    const materialData = await this.indexDBService.getMaterialDataByGuid(materialDataGuid);
    
    if (!materialData || materialData.systemChildrenCollection.length === CONSTANTS.ZERO) {
      return;
    }

    // Process all children (materials and subsystems)
    this.processSystemChildren(materialData, CONSTANTS.ONE);
  }

  private processSystemChildren(materialData: MaterialData, typicalOfCount: number): void {
    for (const child of materialData.systemChildrenCollection) {
      
      if (child.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        this.processSubSystemForMaterialByCountry(child as SubSystem, typicalOfCount);
      } else {
        this.processMaterialItemForCountry(child as Material, CONSTANTS.ONE, typicalOfCount);
      }
    }
  }

  private processSubSystemForMaterialByCountry(subSystem: SubSystem, typicalOfCount: number): void {
    
    // Process subsystem materials
    for (const materialItem of subSystem.children) {
      this.processMaterialItemForCountry(materialItem as Material, subSystem.quantity, typicalOfCount);
    }
  }

  private processMaterialItemForCountry(materialItem: Material, multiplier: number = CONSTANTS.ONE, typicalOfCount: number): void {
    // Only master material rows should contribute to country totals.
    if (!materialItem.isMaster) {
      return;
    }

    const quantity = materialItem.quantity * multiplier * typicalOfCount;
    const unitCost = materialItem.cost;
    
    // Initial validation - both quantity and cost must be greater than 0
    if (quantity === CONSTANTS.ZERO || unitCost === CONSTANTS.ZERO) {
      return;
    }

    const country = materialItem.orig;
    const totalCost = quantity * unitCost;

    this.addMaterialByCountryMap(materialItem, country, quantity, unitCost, totalCost);
  }

  private addMaterialByCountryMap(child: Material, country: string, quantity: number, unitCost: number, totalCost: number): void {

    if(this.reportsCommonService.isGenericNameApplicable()) {
      child.description = this.reportsCommonService.getGenericNameBySSN(child.ssn) || child.description;
    }
    const modelNumber = child.modelNumber || '';
    const manufacturerPart = child.mpn || '';
    // Create aggregation key based on expanded/normal mode
    const aggregationKey = this.selectedReportCheckboxes.expanded.checked
      ? `${child.referenceNumber}::${child.modelNumber}::${child.mpn}::${child.description}::${unitCost}`
      : `${child.referenceNumber}::${child.description}::${unitCost}`;

    // Initialize country map if not exists
    if (!this.countryMaterialMaps.has(country)) {
      this.countryMaterialMaps.set(country, new Map<string, MaterialCostData>());
      this.countryTotals.set(country, CONSTANTS.ZERO);
    }

    const countryMaterials = this.countryMaterialMaps.get(country)!;
    
    if (countryMaterials.has(aggregationKey)) {
      // Aggregate existing material
      const existingData = countryMaterials.get(aggregationKey)!;
      existingData.quantity += quantity;
      existingData.totalCost += totalCost;
    } else {
      // Add new material
      const materialCostData: MaterialCostData = {
        rowIndex: countryMaterials.size,
        referenceNumber: child.referenceNumber || '',
        modelNumber,
        manufacturerPart,
        description: child.description || '',
        quantity: quantity,
        unitCost: unitCost,
        totalCost: totalCost,
        materialIndicator: this.reportsCommonService.generateMaterialIndicator(child)
      };
      countryMaterials.set(aggregationKey, materialCostData);
    }
    
    // Update country total (done for both new and aggregated materials)
    const currentTotal = this.countryTotals.get(country)!;
    this.countryTotals.set(country, currentTotal + totalCost);
  }

  private createCountryTables(): void {
    // Sort countries alphabetically
    const sortedCountries = Array.from(this.countryMaterialMaps.keys())
      .map(country => ({ country, headerName: country?.trim() || CONSTANTS.UNKNOWN_COO }))
      .sort((firstCountry, secondCountry) => firstCountry.headerName.localeCompare(secondCountry.headerName, undefined, { sensitivity: 'base' }))
      .map(entry => entry.country);

    for (const country of sortedCountries) {
      const countryMaterials = this.countryMaterialMaps.get(country)!;
      const countryTotal = this.countryTotals.get(country)!;

      // Create tables for both expanded and normal modes
      this.createCountryTable(country, true);  // Expanded
      this.createCountryTable(country, false); // Normal
      
      const expandedTableIndex = this.materialByCountryInfoExpandedList.length - CONSTANTS.ONE;
      const normalTableIndex = this.materialByCountryInfoList.length - CONSTANTS.ONE;

      // Convert materials to array in order of first encounter (no sorting)
      const materialsArray = Array.from(countryMaterials.values());

      // Add material rows to both lists
      for (const material of materialsArray) {
        this.addMaterialRow(expandedTableIndex, material, true);  // Expanded
        this.addMaterialRow(normalTableIndex, material, false);   // Normal
      }

      // Add country total footer to both lists
      const isUnknownCoo = !country?.trim();
      this.addCountryFooter(expandedTableIndex, countryTotal, true, isUnknownCoo);  // Expanded
      this.addCountryFooter(normalTableIndex, countryTotal, false, isUnknownCoo);   // Normal
    }
  }

  private createCountryTable(countryName: string, isExpanded: boolean): void {
    const countryHeader = countryName?.trim() || CONSTANTS.UNKNOWN_COO;
    const title = this.createTitleHeaderRow(isExpanded);
    const tableHeader = this.reportsCommonService.generateReportTableHeader(`${countryHeader} ${this.translate.instant('PARTS')}`);
    tableHeader.forEach(headerRow => {
      headerRow.headers.forEach(header => {
        header.columnspan = title.length;
        header.italics = true;
      });
    });

    const tableData: ITableData = {
      tableHeader,
      title,
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    };
    
    if (isExpanded) {
      this.materialByCountryInfoExpandedList.push(tableData);
    } else {
      this.materialByCountryInfoList.push(tableData);
    }
  }

  private createTitleHeaderRow(isExpanded: boolean): IDataItem[] {
    if (isExpanded) {
      return [
        { data: this.translate.instant('REF_#'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "130" },
        { data: this.translate.instant('MODEL_#'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "80" },
        { data: this.translate.instant('MANUFACTURER_PART_#'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "130" },
        { data: this.translate.instant('DESCRIPTION'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.translate.instant('Quantity'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "40" },
        { data: this.translate.instant('UNIT_COST'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "60" },
        { data: this.translate.instant('TOTAL_COST'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "60" }
      ];
    } else {
      return [
        { data: this.translate.instant('REF_#'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "130" },
        { data: this.translate.instant('DESCRIPTION'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center" },
        { data: this.translate.instant('Quantity'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "40" },
        { data: this.translate.instant('UNIT_COST'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "60" },
        { data: this.translate.instant('TOTAL_COST'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "60" }
      ];
    }
  }

  private addMaterialRow(tableIndex: number, material: MaterialCostData, isExpanded?: boolean): void {
    const expandedMode = isExpanded !== undefined ? isExpanded : this.selectedReportCheckboxes.expanded.checked;
    const refCell = this.createCell(material.referenceNumber || '', CONSTANTS.ZERO, 'left', material.materialIndicator);
    //TODO: to be removed later
    // const refCell: IDataItem = { data: material.referenceNumber || '', columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, materialIndicator: material.referenceNumber.length < 10 ?'RPN':'APN' };
    const descCell = this.createCell(material.description || '', CONSTANTS.ZERO, 'left');
    const qtyCell = this.createCell(material.quantity.toString(), CONSTANTS.ZERO, 'center');
    const unitCostCell = this.createCell(this.formatCurrency(material.unitCost), CONSTANTS.ZERO, 'right');
    const totalCostCell = this.createCell(this.formatCurrency(material.totalCost), CONSTANTS.ZERO, 'right');
    
    let rowData: IDataItem[];
    
    if (expandedMode) {
      const modelCell = this.createCell(material.modelNumber || '', CONSTANTS.ZERO, 'left');
      const mfgPartCell = this.createCell(material.manufacturerPart || '', CONSTANTS.ZERO, 'left');
      
      rowData = [refCell, modelCell, mfgPartCell, descCell, qtyCell, unitCostCell, totalCostCell];
    } else {
      rowData = [refCell, descCell, qtyCell, unitCostCell, totalCostCell];
    }
    
    // Add to the appropriate list
    if (expandedMode) {
      this.materialByCountryInfoExpandedList[tableIndex].data.push({ data: rowData });
    } else {
      this.materialByCountryInfoList[tableIndex].data.push({ data: rowData });
    }
  }

  private createCell(text: string, hierarchyLevel: number, alignment: ReportTextAlignment, materialIndicator?: string): IDataItem {
    return {
      data: text,
      columnspan: CONSTANTS.ONE,
      hierarchyLevel: hierarchyLevel,
      alignment: alignment,
      materialIndicator: this.getMaterialIndicator(materialIndicator)
    };
  }

  private addCountryFooter(tableIndex: number,   total: number, isExpanded: boolean, isUnknownCoo: boolean): void {
    const currentTable = isExpanded ? this.materialByCountryInfoExpandedList[tableIndex] : this.materialByCountryInfoList[tableIndex];
    
    if (isExpanded) {
      currentTable.tableFooter.push({
        headers: [
          { headerText: this.translate.instant('COUNTRY_TOTAL'), columnspan: CONSTANTS.ONE, alignment: 'left' },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
          { headerText: this.formatCurrency(total), columnspan: CONSTANTS.ONE, alignment: 'right' }
        ]
      });
      if (isUnknownCoo) {
        currentTable.tableFooter.push({
          headers: [
            { headerText: this.translate.instant('MATERIAL_BY_COUNTRY_COO_FOOTNOTE'), columnspan: CONSTANTS.SEVEN, alignment: 'left' }
          ]
        });
      }
    } else {
      currentTable.tableFooter.push({
        headers: [
          { headerText: this.translate.instant('COUNTRY_TOTAL'), columnspan: CONSTANTS.ONE, alignment: 'left' },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
          { headerText: '', columnspan: CONSTANTS.ONE, alignment: 'center' },
          { headerText: this.formatCurrency(total), columnspan: CONSTANTS.ONE, alignment: 'right' }
        ]
      });
      if (isUnknownCoo) {
        currentTable.tableFooter.push({
          headers: [
            { headerText: this.translate.instant('MATERIAL_BY_COUNTRY_COO_FOOTNOTE'), columnspan: CONSTANTS.FIVE, alignment: 'left' }
          ]
        });
      }
    }
  }

  private clearMemory(): void {
    this.countryMaterialMaps.clear();
    this.countryTotals.clear();
  }

  private getMaterialIndicator(materialIndicator: string): MaterialIndicators {
      switch(materialIndicator) {
        case CONSTANTS.UPN_INDICATOR:
          return 'UPN';
        case CONSTANTS.RPN_INDICATOR:
          return 'RPN';
        case CONSTANTS.SIPART_INDICATOR:
          return 'SiParts';
        case CONSTANTS.APN_INDICATOR:
          return 'APN';
        default:
          return null;
      }
    }
  private formatCurrency(value: number): string {
    return this.multilingualCurrencyPipe.transform(value);
  }

  ngOnDestroy(): void {
    this.clearMemory();
  }
}
